/* eslint-disable react-refresh/only-export-components */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useMemo } from 'react'
import { Text as PDFText, View, StyleSheet } from '@react-pdf/renderer'
import type { JSONContent as TipTapJSONContent } from '@tiptap/core'

// Helper function to convert HSL CSS variable to hex color for react-pdf
function hslToHex(h: number, s: number, l: number): string {
    l /= 100;
    const a = s * Math.min(l, 1 - l) / 100;
    const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
}

// Get theme colors from CSS variables
function getThemeColors() {
    const root = getComputedStyle(document.documentElement);
    
    const parseHSL = (varName: string): string => {
        const value = root.getPropertyValue(varName).trim();
        const parts = value.split(/[\s,]+/).map(v => parseFloat(v));
        if (parts.length >= 3) {
            return hslToHex(parts[0], parts[1], parts[2]);
        }
        return '#000000';
    };

    return {
        foreground: parseHSL('--foreground'),
        border: parseHSL('--border'),
        muted: parseHSL('--muted-foreground'),
    };
}

interface TiptapJsonToHtmlProps {
    json: string
}

export default ({ json }: TiptapJsonToHtmlProps) => {
    const colors = getThemeColors();
    
    const parsed = useMemo(() => {
        try {
            return JSON.parse(json) as TipTapJSONContent
        } catch {
            return {
                type: 'doc',
                content: [
                    {
                        type: 'paragraph',
                        content: [{ type: 'text', text: json ?? '' }]
                    }
                ]
            } as TipTapJSONContent
        }
    }, [json])

    const styles = StyleSheet.create({
        page: { padding: 24, fontSize: 8, fontFamily: 'Helvetica', color: colors.foreground },
        paragraph: { fontSize: 8, padding: 5, color: colors.foreground },
        heading1: { fontSize: 16, marginBottom: 8, fontWeight: 'bold', color: colors.foreground },
        heading2: { fontSize: 12, marginBottom: 6, fontWeight: 'bold', color: colors.foreground },
        listItemRow: { flexDirection: 'row' },
        listBullet: { marginVertical: 'auto', width: 8, fontSize: 8, color: colors.foreground },
        listContent: { fontSize: 8, color: colors.foreground },
        blockquote: { marginVertical: 8, paddingLeft: 8, borderLeftWidth: 3, borderLeftColor: colors.border, color: colors.muted },
        hr: { height: 1, backgroundColor: colors.border, marginVertical: 8 },
        bold: { fontFamily: 'Helvetica-Bold' },
        italic: { fontFamily: 'Helvetica-Oblique' },
        underline: { textDecoration: 'underline' },
        strike: { textDecoration: 'line-through' }
    })

    const renderContent = (content?: any[]) =>
        (content ?? []).map((node, idx) => renderNode(node, idx))

    const textWithMarks = (node: any, key: number) => {
        const marks = (node.marks ?? []) as any[]
        const textStyles: any[] = []
        if (marks.some(m => m.type === 'bold')) textStyles.push(styles.bold)
        if (marks.some(m => m.type === 'italic')) textStyles.push(styles.italic)
        if (marks.some(m => m.type === 'underline')) textStyles.push(styles.underline)
        if (marks.some(m => m.type === 'strike')) textStyles.push(styles.strike)

        return (
            <PDFText key={key} style={textStyles.length ? textStyles : undefined}>
                {node.text}
            </PDFText>
        )
    }

    const renderNode = (node: any, key: number) => {
        if (!node) return null

        switch (node.type) {
            case 'text':
                return textWithMarks(node, key)

            case 'paragraph':
                return (
                    <PDFText key={key} style={styles.paragraph}>
                        {renderContent(node.content)}
                    </PDFText>
                )

            case 'heading': {
                const level = node.attrs?.level ?? 1
                return (
                    <PDFText key={key} style={level === 1 ? styles.heading1 : styles.heading2}>
                        {renderContent(node.content)}
                    </PDFText>
                )
            }

            case 'bulletList':
                return (
                    <View key={key} style={{ marginBottom: 6 }}>
                        {(node.content ?? []).map((li: any, i: number) => (
                            <View key={i} style={styles.listItemRow}>
                                <PDFText style={styles.listBullet}>•</PDFText>
                                <PDFText style={styles.listContent}>{renderContent(li.content)}</PDFText>
                            </View>
                        ))}
                    </View>
                )

            case 'orderedList':
                return (
                    <View key={key} style={{ marginBottom: 6 }}>
                        {(node.content ?? []).map((li: any, i: number) => (
                            <View key={i} style={styles.listItemRow}>
                                <PDFText style={styles.listBullet}>{`${i + 1}.`}</PDFText>
                                <PDFText style={[styles.listContent]}>{renderContent(li.content)}</PDFText>
                            </View>
                        ))}
                    </View>
                )

            case 'listItem':
                // fallback if listItem is rendered directly
                return (
                    <View key={key} style={styles.listItemRow}>
                        <PDFText style={styles.listBullet}>•</PDFText>
                        <PDFText style={styles.listContent}>{renderContent(node.content)}</PDFText>
                    </View>
                )

            case 'blockquote':
                return (
                    <View key={key} style={styles.blockquote}>
                        {renderContent(node.content)}
                    </View>
                )

            case 'horizontalRule':
                return <View key={key} style={styles.hr} />

            default:
                // try render children for unknown nodes
                return (
                    <View key={key}>
                        {renderContent(node.content)}
                    </View>
                )
        }
    }

    const pdfDoc = useMemo(() => {
        // ensure parsed.content exists (tiptap doc)
        const docContent = parsed?.content ?? []
        return (
            <View>
                {renderContent(docContent)}
            </View>
        )
    }, [parsed])

    return pdfDoc
}